import {useEffect, useMemo, useState} from 'react'
import Select from 'react-select'
import {
  AlertTriangle,
  ArrowDownAZ,
  Bot,
  ChevronsDown,
  ChevronsUp,
  KeyRound,
  MessageSquare,
  Plus,
  Power,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
  X,
} from 'lucide-react'
import * as API from '../../api'
import {PageTitle} from '../../../_metronic/layout/core'
import ConfirmModal from '../modals/ConfirmModal'
import PermissionsModal from './PermissionModal'
import {EmptyState} from './EmptyState'
import {LoadingState} from './LoadingState'
import {PermissionCard} from './PermissionCard'
import {FeatureStatusModal} from './FeatureStatusModal'
import {PermissionStatusSelect} from './PermissionStatusSelect'
import {useAuth} from '../../modules/auth'
import {
  getCategoryLabel,
  getPermissionCategory,
  getPermissionRisk,
  isAgentStudioPermission,
  isPlatformReviewPermission,
  matchesPermissionStatus,
  type Permission,
  type PermissionId,
  type PermissionStatusFilter,
} from './permissionMetadata'

type Props = {
  className?: string
}

type GroupFilter = 'all' | 'agent-studio' | 'platform-review' | 'sensitive'
type GroupSort = 'name-asc' | 'name-desc' | 'permissions-desc'

type PermissionUser = {
  id_user: string | number
  guid?: string
  name: string
  email?: string
  permissions: PermissionId[]
}

type UserOption = {
  value: string
  label: string
  email?: string
}

const parsePermissionIds = (value: unknown): PermissionId[] => {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || !value.trim()) return []

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const PermissionsScreen: React.FC<Props> = ({className}) => {
  const {refreshPermissions} = useAuth()
  const [message, setMessage] = useState('')
  const [permissionModal, showPermissionModal] = useState(false)
  const [featureStatusModal, showFeatureStatusModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<PermissionStatusFilter>('active')
  const [confirmModal, showConfirmModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [groupPermissions, setGroupPermissions] = useState<any[]>([])
  const [users, setUsers] = useState<PermissionUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})
  const [groupSearch, setGroupSearch] = useState('')
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all')
  const [groupSort, setGroupSort] = useState<GroupSort>('name-asc')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [loadError, setLoadError] = useState('')
  const [userFilterError, setUserFilterError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    showConfirmModal(message !== '')
  }, [message])

  const visiblePermissions = useMemo(
    () => permissions.filter((permission) => matchesPermissionStatus(permission, statusFilter)),
    [permissions, statusFilter]
  )
  const permissionMap = useMemo(
    () => new Map(visiblePermissions.map((permission) => [permission.id_feature, permission])),
    [visiblePermissions]
  )

  const selectedUser = useMemo(
    () => users.find((user) => String(user.id_user) === selectedUserId),
    [selectedUserId, users]
  )

  const userOptions = useMemo<UserOption[]>(
    () =>
      users
        .map((user) => ({
          value: String(user.id_user),
          label: user.name,
          email: user.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [users]
  )

  const selectedUserOption = useMemo(
    () => userOptions.find((option) => option.value === selectedUserId) || null,
    [selectedUserId, userOptions]
  )

  const memberCountByGroup = useMemo(() => {
    const counts = new Map<string, number>()
    users.forEach((user) => {
      user.permissions.forEach((groupId) => {
        const key = String(groupId)
        counts.set(key, (counts.get(key) || 0) + 1)
      })
    })
    return counts
  }, [users])

  const filteredGroups = useMemo(() => {
    const normalized = groupSearch.trim().toLocaleLowerCase('pt-BR')
    const selectedUserGroups = new Set(
      (selectedUser?.permissions || []).map((groupId) => String(groupId))
    )

    return groupPermissions.filter((group) => {
      const groupPermissionsData = group.data
        .map((permissionId: PermissionId) => permissionMap.get(permissionId))
        .filter((permission: Permission | undefined): permission is Permission => !!permission)

      const matchesSearch =
        !normalized ||
        group.name.toLocaleLowerCase('pt-BR').includes(normalized) ||
        groupPermissionsData.some((permission) => {
          const searchableText = [
            permission.title,
            permission.name,
            getCategoryLabel(getPermissionCategory(permission.name)),
          ]
            .join(' ')
            .toLocaleLowerCase('pt-BR')
          return searchableText.includes(normalized)
        })

      const matchesGroupFilter =
        groupFilter === 'all' ||
        (groupFilter === 'agent-studio' &&
          groupPermissionsData.some((permission) => isAgentStudioPermission(permission))) ||
        (groupFilter === 'platform-review' &&
          groupPermissionsData.some((permission) => isPlatformReviewPermission(permission))) ||
        (groupFilter === 'sensitive' &&
          groupPermissionsData.some(
            (permission) => getPermissionRisk(permission.name) !== 'standard'
          ))

      const matchesUser = !selectedUser || selectedUserGroups.has(String(group.id_feature_group))

      const matchesStatus = statusFilter === 'all' || groupPermissionsData.length > 0 || group.data.length === 0
      return matchesSearch && matchesGroupFilter && matchesUser && matchesStatus
    })
  }, [groupFilter, groupPermissions, groupSearch, permissionMap, selectedUser, statusFilter])

  const visibleGroups = useMemo(() => {
    return [...filteredGroups].sort((a, b) => {
      if (groupSort === 'permissions-desc') {
        const count = (group: any) => group.data.filter((id: PermissionId) => permissionMap.has(id)).length
        return count(b) - count(a) || a.name.localeCompare(b.name, 'pt-BR')
      }
      const comparison = a.name.localeCompare(b.name, 'pt-BR')
      return groupSort === 'name-desc' ? -comparison : comparison
    })
  }, [filteredGroups, groupSort, permissionMap])

  const agentStudioGroupCount = useMemo(
    () =>
      groupPermissions.filter((group) =>
        group.data.some((permissionId: PermissionId) => {
          const permission = permissionMap.get(permissionId)
          return permission ? isAgentStudioPermission(permission) : false
        })
      ).length,
    [groupPermissions, permissionMap]
  )

  const totalAssignments = useMemo(
    () => groupPermissions.reduce((total: number, group: any) =>
      total + group.data.filter((id: PermissionId) => permissionMap.has(id)).length, 0),
    [groupPermissions, permissionMap]
  )

  const platformReviewGroupCount = useMemo(
    () =>
      groupPermissions.filter((group) =>
        group.data.some((permissionId: PermissionId) => {
          const permission = permissionMap.get(permissionId)
          return permission ? isPlatformReviewPermission(permission) : false
        })
      ).length,
    [groupPermissions, permissionMap]
  )

  const hasActiveFilters = !!groupSearch || groupFilter !== 'all' || !!selectedUserId || statusFilter !== 'active'
  const allExpanded =
    visibleGroups.length > 0 &&
    visibleGroups.every((group) => expandedCards[String(group.id_feature_group)])
  const allCollapsed =
    visibleGroups.length > 0 &&
    visibleGroups.every((group) => !expandedCards[String(group.id_feature_group)])

  const loadData = async () => {
    setIsLoading(true)
    setLoadError('')
    setUserFilterError('')

    try {
      const usersRequest = API.listUsers().catch((error) => {
        console.warn('User filter unavailable:', error)
        return null
      })
      const [permissionsRes, groupPermissionsRes, usersRes] = await Promise.all([
        API.getPermissions(),
        API.getGroupPermissions(),
        usersRequest,
      ])

      if (!permissionsRes.success || !groupPermissionsRes.success) {
        throw new Error('Não foi possível carregar as permissões.')
      }

      setPermissions(Array.isArray(permissionsRes.data) ? permissionsRes.data : [])
      const groupsData = (
        Array.isArray(groupPermissionsRes.data) ? groupPermissionsRes.data : []
      ).map((item: any) => ({
        ...item,
        data: parsePermissionIds(item.data),
      }))
      setGroupPermissions(groupsData)

      if (usersRes && Array.isArray(usersRes.data)) {
        setUsers(
          usersRes.data.map((user: any) => ({
            ...user,
            permissions: parsePermissionIds(user.permissions),
          }))
        )
      } else {
        setUsers([])
        setSelectedUserId('')
        setUserFilterError('O filtro por usuário está indisponível no momento.')
      }

      setExpandedCards(
        groupsData.reduce((acc: Record<string, boolean>, group: any) => {
          acc[String(group.id_feature_group)] = false
          return acc
        }, {})
      )
    } catch (error) {
      console.error('Error loading data:', error)
      setLoadError('Não foi possível carregar os grupos de permissões.')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleCard = (id: PermissionId) => {
    const key = String(id)
    setExpandedCards((previous) => ({...previous, [key]: !previous[key]}))
  }

  const expandAll = () => {
    setExpandedCards((previous) => ({
      ...previous,
      ...visibleGroups.reduce((acc: Record<string, boolean>, group) => {
        acc[String(group.id_feature_group)] = true
        return acc
      }, {}),
    }))
  }

  const collapseAll = () => {
    setExpandedCards((previous) => ({
      ...previous,
      ...visibleGroups.reduce((acc: Record<string, boolean>, group) => {
        acc[String(group.id_feature_group)] = false
        return acc
      }, {}),
    }))
  }

  const clearFilters = () => {
    setStatusFilter('active')
    setGroupSearch('')
    setGroupFilter('all')
    setSelectedUserId('')
  }

  const handleNewGroup = () => {
    setSelectedItem(null)
    showPermissionModal(true)
  }

  const handleEditGroup = (group: any) => {
    setSelectedItem(group)
    showPermissionModal(true)
  }

  const handleDeleteGroup = (group: any) => {
    setSelectedItem(group)
    setMessage(
      'Deseja remover esse grupo de permissões? Caso existam usuários vinculados a ele, eles serão afetados por essa ação.'
    )
  }

  const removeItem = async (item: any) => {
    setIsLoading(true)
    try {
      await API.removeGroupPermissions(item)
      await loadData()
    } catch (error) {
      console.error('Error removing group:', error)
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className={'container mx-auto px-4 py-5 ' + (className || '')}>
        <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <PageTitle>Grupos de Permissões</PageTitle>
            <p className='mt-1 text-sm text-gray-500'>
              Encontre, compare e gerencie os perfis de acesso do sistema.
            </p>
          </div>
          <div className='flex flex-wrap gap-2'>
            <button
              type='button'
              onClick={() => showFeatureStatusModal(true)}
              disabled={isLoading || !!loadError}
              className='inline-flex items-center justify-center rounded-lg border border-blue-200 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50'
            >
              <Power className='mr-2 h-4 w-4' /> Gerenciar features
            </button>
            <button
              type='button'
              onClick={handleNewGroup}
              className='inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
            >
              <Plus className='mr-2 h-4 w-4' />
              Criar grupo
            </button>
          </div>
        </div>

        {!isLoading && !loadError && groupPermissions.length > 0 && (
          <div className='mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-sm'>
            <span className='inline-flex items-center gap-2 text-xs text-gray-500'>
              <ShieldCheck className='h-4 w-4 text-blue-600' />
              <strong className='text-sm text-gray-900'>{groupPermissions.length}</strong> grupos
            </span>
            <span className='inline-flex items-center gap-2 text-xs text-gray-500'>
              <Bot className='h-4 w-4 text-sky-600' />
              <strong className='text-sm text-gray-900'>{agentStudioGroupCount}</strong> com Agent
              Studio
            </span>
            <span className='inline-flex items-center gap-2 text-xs text-gray-500'>
              <MessageSquare className='h-4 w-4 text-sky-600' />
              <strong className='text-sm text-gray-900'>{platformReviewGroupCount}</strong> com
              Platform Review
            </span>
            <span className='inline-flex items-center gap-2 text-xs text-gray-500'>
              <KeyRound className='h-4 w-4 text-violet-600' />
              <strong className='text-sm text-gray-900'>{totalAssignments}</strong> permissões
              atribuídas
            </span>
            {users.length > 0 && (
              <span className='inline-flex items-center gap-2 text-xs text-gray-500'>
                <Users className='h-4 w-4 text-emerald-600' />
                <strong className='text-sm text-gray-900'>{users.length}</strong> usuários
              </span>
            )}
          </div>
        )}

        {isLoading ? (
          <LoadingState />
        ) : loadError ? (
          <div className='rounded-xl border border-red-200 bg-red-50 p-6 text-center'>
            <p className='text-sm font-semibold text-red-700'>{loadError}</p>
            <button
              type='button'
              onClick={loadData}
              className='mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700'
            >
              Tentar novamente
            </button>
          </div>
        ) : groupPermissions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <section className='mb-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm'>
              <div className='grid grid-cols-1 gap-3 lg:grid-cols-12'>
                <label className='lg:col-span-5'>
                  <span className='mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-gray-500'>
                    Buscar grupos e permissões
                  </span>
                  <span className='relative block'>
                    <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                    <input
                      type='search'
                      placeholder='Nome do grupo, permissão, categoria ou identificador...'
                      value={groupSearch}
                      onChange={(event) => setGroupSearch(event.target.value)}
                      className='h-10 w-full rounded-lg border border-gray-300 bg-gray-50 pl-9 pr-9 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100'
                    />
                    {groupSearch && (
                      <button
                        type='button'
                        onClick={() => setGroupSearch('')}
                        className='absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        aria-label='Limpar busca'
                      >
                        <X className='h-3.5 w-3.5' />
                      </button>
                    )}
                  </span>
                </label>

                <div className='lg:col-span-4'>
                  <span className='mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-gray-500'>
                    Filtrar por usuário
                  </span>
                  <Select<UserOption>
                    value={selectedUserOption}
                    options={userOptions}
                    onChange={(option) => setSelectedUserId(option?.value || '')}
                    isClearable
                    isSearchable
                    isDisabled={users.length === 0}
                    placeholder={
                      users.length > 0 ? 'Selecione nome ou e-mail' : 'Usuários indisponíveis'
                    }
                    noOptionsMessage={() => 'Nenhum usuário encontrado'}
                    className='text-sm'
                    classNamePrefix='permission-user-select'
                    formatOptionLabel={(option) => (
                      <div className='min-w-0'>
                        <div className='truncate text-sm font-medium text-gray-800'>
                          {option.label}
                        </div>
                        {option.email && (
                          <div className='truncate text-[11px] text-gray-400'>{option.email}</div>
                        )}
                      </div>
                    )}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '40px',
                        borderRadius: '0.5rem',
                        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                        backgroundColor: state.isFocused ? '#ffffff' : '#f9fafb',
                        boxShadow: state.isFocused ? '0 0 0 2px #dbeafe' : 'none',
                        '&:hover': {borderColor: '#93c5fd'},
                      }),
                      valueContainer: (base) => ({...base, padding: '1px 10px'}),
                      dropdownIndicator: (base) => ({...base, padding: '7px'}),
                      clearIndicator: (base) => ({...base, padding: '7px'}),
                      menu: (base) => ({...base, zIndex: 20}),
                    }}
                  />
                </div>

                <label className='lg:col-span-3'>
                  <span className='mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-gray-500'>
                    Tipo de acesso
                  </span>
                  <span className='relative block'>
                    <SlidersHorizontal className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                    <select
                      value={groupFilter}
                      onChange={(event) => setGroupFilter(event.target.value as GroupFilter)}
                      className='h-10 w-full appearance-none rounded-lg border border-gray-300 bg-gray-50 pl-9 pr-8 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100'
                    >
                      <option value='all'>Todos os tipos</option>
                      <option value='agent-studio'>Com Agent Studio</option>
                      <option value='platform-review'>Com Platform Review</option>
                      <option value='sensitive'>Com acessos sensíveis</option>
                    </select>
                  </span>
                </label>
              </div>

              <div className='mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex min-w-0 flex-wrap items-center gap-2 text-xs text-gray-500'>
                  <span>
                    <strong className='text-gray-900'>{visibleGroups.length}</strong> de{' '}
                    {groupPermissions.length} grupos
                  </span>
                  {selectedUser && (
                    <span className='inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-blue-700'>
                      <UserRound className='h-3.5 w-3.5 flex-shrink-0' />
                      <span className='truncate'>{selectedUser.name}</span>
                      <button
                        type='button'
                        onClick={() => setSelectedUserId('')}
                        className='rounded-full p-0.5 hover:bg-blue-100'
                        aria-label='Remover filtro de usuário'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </span>
                  )}
                  {userFilterError && (
                    <span className='inline-flex items-center gap-1 text-amber-600'>
                      <AlertTriangle className='h-3.5 w-3.5' /> {userFilterError}
                    </span>
                  )}
                  {hasActiveFilters && (
                    <button
                      type='button'
                      onClick={clearFilters}
                      className='font-semibold text-blue-600 hover:text-blue-700 hover:underline'
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>

                <div className='flex flex-wrap items-center gap-1.5'>
                  <PermissionStatusSelect value={statusFilter} onChange={setStatusFilter} />
                  <label className='relative'>
                    <ArrowDownAZ className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400' />
                    <select
                      value={groupSort}
                      onChange={(event) => setGroupSort(event.target.value as GroupSort)}
                      aria-label='Ordenar grupos'
                      className='h-8 appearance-none rounded-md border border-gray-200 bg-white pl-8 pr-7 text-xs font-medium text-gray-600 outline-none hover:bg-gray-50 focus:border-blue-500'
                    >
                      <option value='name-asc'>Nome A–Z</option>
                      <option value='name-desc'>Nome Z–A</option>
                      <option value='permissions-desc'>Mais permissões</option>
                    </select>
                  </label>
                  <button
                    type='button'
                    onClick={expandAll}
                    disabled={allExpanded || visibleGroups.length === 0}
                    className='inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <ChevronsDown className='h-3.5 w-3.5' /> Expandir
                  </button>
                  <button
                    type='button'
                    onClick={collapseAll}
                    disabled={allCollapsed || visibleGroups.length === 0}
                    className='inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <ChevronsUp className='h-3.5 w-3.5' /> Recolher
                  </button>
                </div>
              </div>
            </section>

            {visibleGroups.length === 0 ? (
              <div className='rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center'>
                <div className='mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm'>
                  <Search className='h-5 w-5' />
                </div>
                <p className='text-sm font-semibold text-gray-700'>Nenhum grupo encontrado</p>
                <p className='mt-1 text-xs text-gray-500'>
                  {selectedUser
                    ? `${selectedUser.name} não participa de grupos com os outros filtros selecionados.`
                    : 'Tente ajustar a busca ou remover algum filtro.'}
                </p>
                {hasActiveFilters && (
                  <button
                    type='button'
                    onClick={clearFilters}
                    className='mt-3 text-xs font-semibold text-blue-600 hover:underline'
                  >
                    Limpar todos os filtros
                  </button>
                )}
              </div>
            ) : (
              <div className='grid grid-cols-1 gap-2'>
                {visibleGroups.map((group) => (
                  <PermissionCard
                    key={group.id_feature_group}
                    group={group}
                    permissions={visiblePermissions}
                    onEdit={handleEditGroup}
                    onDelete={handleDeleteGroup}
                    isExpanded={expandedCards[String(group.id_feature_group)]}
                    onToggleExpand={() => toggleCard(group.id_feature_group)}
                    memberCount={
                      users.length > 0
                        ? memberCountByGroup.get(String(group.id_feature_group)) || 0
                        : undefined
                    }
                    searchTerm={groupSearch}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {permissionModal && (
        <PermissionsModal
          data={permissions}
          group={selectedItem}
          initialStatusFilter={statusFilter}
          onClose={() => {
            showPermissionModal(false)
            setSelectedItem(null)
          }}
          onChange={() => {
            loadData()
            showPermissionModal(false)
            setSelectedItem(null)
          }}
        />
      )}

      {featureStatusModal && (
        <FeatureStatusModal
          permissions={permissions}
          onClose={() => showFeatureStatusModal(false)}
          onSaved={(changes) => {
            const byId = new Map(changes.map((change) => [String(change.id_feature), change.is_active]))
            setPermissions((current) => current.map((permission) => ({
              ...permission,
              is_active: byId.get(String(permission.id_feature)) ?? permission.is_active,
            })))
            showFeatureStatusModal(false)
            void refreshPermissions(true)
          }}
        />
      )}

      {confirmModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm'>
          <ConfirmModal
            title={`Grupo: ${selectedItem?.name}`}
            message={message}
            onClose={(value: boolean) => {
              if (value) removeItem(selectedItem)
              setSelectedItem(null)
              setMessage('')
            }}
          />
        </div>
      )}
    </>
  )
}

export default PermissionsScreen
